/**
 * Privacy Policy Page
 */

import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Cookie } from 'lucide-react';

export default function PrivacyPolicyPage(): JSX.Element {
  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Shield size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                Privacy Policy
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="card p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Introduction
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Foxie Systems ("we", "our", or "us") respects your privacy and is committed to protecting your personal data.
              This privacy policy explains how we collect, use, and safeguard your information when you use our platform.
            </p>
          </section>

          {/* Section 1 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Database size={24} className="text-blue-600 dark:text-blue-400" />
              1. Information We Collect
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2">
                Information you provide:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Account Information:</strong> Email address, display name, password (encrypted)</li>
                <li><strong>Payment Information:</strong> Processed securely by our payment providers (Stripe, PayPal)</li>
                <li><strong>Contact Information:</strong> When you contact us via email or contact form</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2">
                Information collected automatically:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Usage Data:</strong> Pages visited, time spent, features used</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                <li><strong>Analytics:</strong> We use Firebase Analytics to understand usage patterns</li>
                <li><strong>Download Activity:</strong> When and how many times you download products</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Eye size={24} className="text-purple-600 dark:text-purple-400" />
              2. How We Use Your Information
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Provide and maintain our services</li>
                <li>Process your purchases and deliver digital products</li>
                <li>Send you order confirmations and account notifications</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Improve our platform and user experience</li>
                <li>Prevent fraud and ensure security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Lock size={24} className="text-green-600 dark:text-green-400" />
              3. Data Storage & Security
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We implement industry-standard security measures:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Encryption:</strong> All data is transmitted over SSL/TLS encryption</li>
                <li><strong>Secure Storage:</strong> Data is stored on Google Cloud Platform (Firebase)</li>
                <li><strong>Access Control:</strong> Strict access controls and authentication requirements</li>
                <li><strong>Payment Security:</strong> We never store full payment card details</li>
                <li><strong>Regular Audits:</strong> Security monitoring and audit logging</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              4. Data Sharing & Third Parties
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Payment Processors:</strong> Stripe, PayPal (for payment processing only)</li>
                <li><strong>Email Services:</strong> Resend (for transactional emails)</li>
                <li><strong>Analytics:</strong> Firebase Analytics (anonymized usage data)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>We do NOT sell your personal data to third parties.</strong>
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Cookie size={24} className="text-amber-600 dark:text-amber-400" />
              5. Cookies & Local Storage
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use cookies and browser local storage for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Authentication and session management</li>
                <li>Shopping cart persistence</li>
                <li>User preferences (theme, currency)</li>
                <li>Analytics and performance monitoring</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You can control cookies through your browser settings, but this may affect functionality.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              6. Your Rights
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain data processing activities</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To exercise these rights, contact us at{' '}
                <a href="mailto:privacy@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">
                  privacy@foxie.systems
                </a>
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              7. Data Retention
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We retain your data for as long as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Your account is active</li>
                <li>Required to provide services (e.g., access to purchased products)</li>
                <li>Necessary for legal, tax, or accounting purposes (typically 7 years)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                After account deletion, we may retain anonymized analytics data.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              8. Children's Privacy
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Our services are not intended for users under 18 years of age. We do not knowingly collect data from children.
                If you believe we have collected data from a minor, please contact us immediately.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              9. Changes to This Policy
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated
                "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              10. Contact Us
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have questions about this Privacy Policy or our data practices:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Email: <a href="mailto:privacy@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">privacy@foxie.systems</a></li>
                <li>Contact Form: <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">Visit our contact page</Link></li>
              </ul>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
            Terms of Service
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/refund-policy" className="text-primary-600 dark:text-primary-400 hover:underline">
            Refund Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
